import {
  useFaqs,
  useKnowledgeDocument,
  useKnowledgeGaps,
  useKnowledgeSearch,
} from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  EmptyState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@hostesa/ui';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { DocumentList } from './document-list';
import { FaqList } from './faq-list';
import { GapList } from './gap-list';
import { namespace } from './i18n';
import { KnowledgeIndexStatus } from './knowledge-index-status';
import { asNumber } from './lib';

/**
 * KnowledgePage — knowledge documents + FAQ + gaps (tabs) plus retrieval search.
 *
 * The catalogue has no document LIST endpoint, so Documents loads a single
 * document by id. FAQs list from the server (spec 42) — active and inactive.
 * Gaps are listed and triaged. The route gates `knowledge:read`; the
 * approve action is additionally gated by `knowledge:approve` at the button
 * level inside DocumentList.
 */
export function KnowledgePage() {
  const { t } = useT(namespace);
  const hotel = useHotelContext();
  const { tenantId, hotelId } = hotel;

  // Server-listed FAQs (spec 42); mutations invalidate the query, so no local state.
  const faqsQuery = useFaqs(tenantId, hotelId);

  // Document loaded by id (detail-only endpoint).
  const [documentInput, setDocumentInput] = useState('');
  const [documentId, setDocumentId] = useState('');
  const documentQuery = useKnowledgeDocument(tenantId, hotelId, documentId);

  // Knowledge gaps drive the index-status summary and the gaps tab.
  const gapsQuery = useKnowledgeGaps(tenantId, hotelId);

  // Retrieval search.
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchResults = useKnowledgeSearch(tenantId, hotelId, searchQuery);

  return (
    <div data-testid="knowledge-page" className="flex flex-col gap-6">
      <PageHeader
        data-testid="knowledge-page-header"
        eyebrow={t('eyebrow')}
        title={t('pageTitle')}
        description={t('pageDescription')}
      />

      {gapsQuery.data && <KnowledgeIndexStatus gaps={gapsQuery.data} />}

      <Tabs defaultValue="documents" data-testid="knowledge-tabs">
        <TabsList data-testid="knowledge-tabs-list">
          <TabsTrigger value="documents" data-testid="knowledge-tab-documents">
            {t('tabs.documents')}
          </TabsTrigger>
          <TabsTrigger value="faqs" data-testid="knowledge-tab-faqs">
            {t('tabs.faqs')}
          </TabsTrigger>
          <TabsTrigger value="gaps" data-testid="knowledge-tab-gaps">
            {t('tabs.gaps')}
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="knowledge-tab-search">
            {t('tabs.search')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" data-testid="knowledge-tab-panel-documents">
          <form
            data-testid="knowledge-document-load-form"
            className="mb-4 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setDocumentId(documentInput.trim());
            }}
          >
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="knowledge-document-id">{t('documents.inputLabel')}</Label>
              <Input
                id="knowledge-document-id"
                data-testid="knowledge-document-id-input"
                placeholder={t('documents.inputPlaceholder')}
                value={documentInput}
                onChange={(e) => setDocumentInput(e.target.value)}
              />
            </div>
            <Button type="submit" data-testid="knowledge-document-open-btn">
              {t('documents.open')}
            </Button>
          </form>

          {documentId.length === 0 && (
            <EmptyState
              data-testid="knowledge-documents-empty"
              title={t('documents.title')}
              description={t('documents.loadHint')}
            />
          )}
          {documentId.length > 0 && documentQuery.isLoading && (
            <LoadingState data-testid="knowledge-document-loading" title={t('states.loading')} />
          )}
          {documentId.length > 0 && documentQuery.isError && (
            <EmptyState
              data-testid="knowledge-document-error"
              title={t('states.errorTitle')}
              description={t('states.errorDescription')}
            />
          )}
          {documentQuery.data && (
            <DocumentList tenantId={tenantId} hotelId={hotelId} document={documentQuery.data} />
          )}
        </TabsContent>

        <TabsContent value="faqs" data-testid="knowledge-tab-panel-faqs">
          {faqsQuery.isLoading && (
            <LoadingState data-testid="knowledge-faqs-loading" title={t('states.loading')} />
          )}
          {faqsQuery.isError && (
            <EmptyState
              data-testid="knowledge-faqs-error"
              title={t('faqs.title')}
              description={t('faqs.error')}
            />
          )}
          {faqsQuery.data && <FaqList faqs={faqsQuery.data.items} />}
        </TabsContent>

        <TabsContent value="gaps" data-testid="knowledge-tab-panel-gaps">
          <GapList
            tenantId={tenantId}
            hotelId={hotelId}
            onFaqCreated={() => void faqsQuery.refetch()}
          />
        </TabsContent>

        <TabsContent value="search" data-testid="knowledge-tab-panel-search">
          <form
            data-testid="knowledge-search-form"
            className="mb-4 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchQuery(searchInput.trim());
            }}
          >
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="knowledge-search">{t('search.label')}</Label>
              <Input
                id="knowledge-search"
                data-testid="knowledge-search-input"
                placeholder={t('search.placeholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <Button type="submit" data-testid="knowledge-search-submit-btn">
              {t('search.submit')}
            </Button>
          </form>

          {searchQuery.length > 0 && searchResults.isLoading && (
            <LoadingState data-testid="knowledge-search-loading" title={t('states.loading')} />
          )}
          {searchQuery.length > 0 &&
            !searchResults.isLoading &&
            (searchResults.data?.length ?? 0) === 0 && (
              <EmptyState
                data-testid="knowledge-search-empty"
                title={t('search.resultsTitle')}
                description={t('search.empty')}
              />
            )}
          {(searchResults.data?.length ?? 0) > 0 && (
            <ul data-testid="knowledge-search-results" className="flex flex-col gap-3">
              {searchResults.data?.map((result) => {
                const score = asNumber(result.score);
                return (
                  <li
                    key={result.id}
                    data-testid={`knowledge-search-result-${result.id}`}
                    className="rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
                  >
                    <p className="text-sm text-[var(--color-charcoal)]">{result.content}</p>
                    {score !== null && (
                      <p className="mt-2 text-xs text-[var(--color-stone-mid)]">
                        {t('search.scoreLabel')}: {score.toFixed(2)}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
